import React, { useState, useEffect } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../Environment/firebase";
import {
  Typography,
  List,
  ListItem,
  Link,
  Box,
  Container,
  Tabs,
  Tab,
  Paper,
  Grid,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom"; // useLocationを追加
import { lightBlue } from "@mui/material/colors";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CatalogueTab from "./CatalogueTab";

type Disease = {
  id: string;
  name: string;
  tag: string;
  definition: string;
  common_age: string;
  symptom: string;
  case_list: string[];
  furigana: string;
};

const Disease = () => {
  const [tags, setTags] = useState<string[]>([]);
  const [selectedTag, setSelectedTag] = useState<string>("");
  const [groupedDiseases, setGroupedDiseases] = useState<{
    [key: string]: Disease[];
  }>({});
  const [message, setMessage] = useState<string>("");
  const [tabIndex, setTabIndex] = useState<number>(0);
  const navigate = useNavigate();
  const location = useLocation();
  const useWindowSize = () => {
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
      const handleResize = () => setWindowWidth(window.innerWidth);
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);

    return windowWidth;
  };

  // クエリパラメータから状態を読み取って初期状態を設定
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "search"));
        const tagsSet = new Set<string>();
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.tag) {
            tagsSet.add(data.tag);
          }
        });
        const fetchedTags = [...tagsSet];
        setTags(fetchedTags);

        const queryParams = new URLSearchParams(location.search);
        const initialTag = queryParams.get("tag") || fetchedTags[0];
        const initialIndex = fetchedTags.indexOf(initialTag);

        if (initialIndex !== -1) {
          setSelectedTag(initialTag);
          handleTagClick(initialTag, initialIndex);
        }
      } catch (error) {
        console.error("エラーが発生しました:", error);
        setMessage("データの取得中にエラーが発生しました。");
      }
    };

    fetchTags();
  }, [location.search]);

  const handleTagClick = async (tag: string, index: number) => {
    setSelectedTag(tag);
    setTabIndex(index);

    // クエリパラメータを更新して、現在の選択状態をURLに保存
    const params = new URLSearchParams(location.search);
    params.set("tag", tag);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });

    try {
      const q = query(collection(db, "search"), where("tag", "==", tag));
      const querySnapshot = await getDocs(q);
      const diseaseGroups: { [key: string]: Disease[] } = {};

      const categories: { [key: string]: string[] } = {
        あ: ["あ", "い", "う", "え", "お"],
        か: ["か", "き", "く", "け", "こ", "が", "ぎ", "ぐ", "げ", "ご"],
        さ: ["さ", "し", "す", "せ", "そ", "ざ", "じ", "ず", "ぜ", "ぞ"],
        た: ["た", "ち", "つ", "て", "と","だ", "ぢ", "づ", "で", "ど"],
        な: ["な", "に", "ぬ", "ね", "の"],
        は: ["は", "ひ", "ふ", "へ", "ほ", "ば", "び", "ぶ", "べ", "ぼ", "ぱ", "ぴ", "ぷ", "ぺ", "ぽ"],
        ま: ["ま", "み", "む", "め", "も"],
        や: ["や", "ゆ", "よ"],
        ら: ["ら", "り", "る", "れ", "ろ"],
        わ: ["わ", "を", "ん"],
      };

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const disease: Disease = {
          id: doc.id,
          name: data.name,
          tag: data.tag,
          definition: data.definition,
          common_age: data.common_age || "情報なし",
          symptom: data.symptom || "情報なし",
          case_list: data.case_list,
          furigana: data.furigana || "その他",
        };

        const firstChar = disease.furigana.charAt(0);
        for (const key in categories) {
          if (categories[key].includes(firstChar)) {
            if (!diseaseGroups[key]) {
              diseaseGroups[key] = [];
            }
            diseaseGroups[key].push(disease);
            break;
          }
        }
      });

      for (const key in diseaseGroups){
        diseaseGroups[key].sort((a, b) => a.furigana.localeCompare(b.furigana, 'ja'));
      }

      setGroupedDiseases(diseaseGroups);
    } catch (error) {
      console.error("エラーが発生しました:", error);
      setMessage("病名の取得中にエラーが発生しました。");
    }
  };

  const handleDiseaseClick = (diseaseId: string) => {
    navigate(`/catalogue/disease/${diseaseId}`); // 現在のタグをURLに追加
  };

  const windowWidth = useWindowSize(); // 現在のウィンドウ幅を取得
  const isMobile = windowWidth < 600;

  return (
    <React.Fragment>
      <CatalogueTab initialTab={1} />
      <Box
        sx={{
          display: "flex",
          minHeight: "95vh",
          minWidth: "100vw",
          textAlign: "center",
          backgroundColor: "#f5f5f5",
          flexDirection: isMobile ? "column" : "row",
        }}
      >
        <Paper
          sx={{
            mt: 2,
            mb: isMobile ? 1 : 2,
            ml: 2,
            mr: isMobile ? 2 : 1,
            boxShadow: 3,
            borderRadius: 2,
          }}
        >
          <Grid
            container
            sx={{ backgroundColor: lightBlue[200], px: 2.5, py: 1.5, borderRadius: 1 }}
          >
            <Typography
              variant="h5"
              textAlign={"center"}
              fontWeight={"bold"}
              color={"white"}
            >
              診療科
            </Typography>
          </Grid>
          {message && (
            <Typography variant="body1" color="error" sx={{ mt: 2 }}>
              {message}
            </Typography>
          )}
          <Tabs
            orientation={isMobile ? "horizontal" : "vertical"}
            value={tabIndex}
            variant="scrollable"
            onChange={(event, newValue) => setTabIndex(newValue)}
            sx={{ borderRight: 1, borderColor: "divider" }}
            TabIndicatorProps={{
              style: {
                backgroundColor: "transparent",
              },
            }}
          >
            {tags.map((tag, index) => (
              <Tab
                key={tag}
                label={
                  <Box
                    display="flex"
                    flexDirection="row" 
                    justifyContent="space-between" 
                    alignItems="center" 
                    sx={{
                      width: "100%",
                    }}
                  >
                    <Box>{tag}</Box>
                    <Box>
                      {isMobile ? (
                        <KeyboardArrowDownIcon />
                      ) : (
                        <KeyboardArrowRightIcon />
                      )}
                    </Box>
                  </Box>
                }
                onClick={() => handleTagClick(tag, index)}
                sx={{
                  mx: 0.5,
                  mt: 0.5,
                  borderRadius: 1,
                  backgroundColor:
                    tabIndex === index ? "lightgrey" : "transparent",
                  "&:hover": {
                    backgroundColor:
                      tabIndex === index ? "lightgrey" : "rgba(0, 0, 0, 0.08)",
                  },
                }}
              />
            ))}
          </Tabs>
        </Paper>
        <Box
          sx={{
            flexGrow: 1,
            mt: isMobile ?1 : 2,
            mb: 2,   
            ml: isMobile ? 2 : 1,
            mr: 2,
            p: 1,
            boxShadow: 3,
            backgroundColor: "white",
            borderRadius: 2,
          }}
        >
          {selectedTag && (
            <Container>
              <Grid container spacing={2} sx={{ mt: 1, mb: 10 }}>
                {Object.keys(groupedDiseases)
                  .sort((a, b) => a.localeCompare(b, "ja"))
                  .map((gyou) => (
                    <Grid item xs={6} key={gyou}>
                      <Typography
                        fontWeight={"bold"}
                        variant="h5"
                        align="left"
                        sx={{ mt: 2, pl: 2 }}
                      >
                        {gyou}
                      </Typography>
                      <List>
                        {groupedDiseases[gyou].map((disease) => (
                          <ListItem key={disease.id}>
                            <Link
                              onClick={() => handleDiseaseClick(disease.id)}
                              sx={{
                                cursor: "pointer",
                                textAlign: "left",
                                width: "100%",
                              }}
                              underline="none"
                            >
                              {disease.name}
                            </Link>
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                  ))}
              </Grid>
            </Container>
          )}
        </Box>
      </Box>
    </React.Fragment>
  );
};

export default Disease;
